import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { ResponsiveContainer, AreaChart, Area, Line, LineChart, RadialBarChart, RadialBar, PolarAngleAxis, BarChart, Bar, XAxis, Tooltip } from 'recharts';
import { ArrowUpRight, ArrowDownRight, Minus, ArrowRight, TrendingUp, Coins, Receipt, Activity, Target, Crown, AlertTriangle, UserCheck, Eye, Search, FileSpreadsheet, FileText, X } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ===== Money formatting =====
const fmt = (val, currency = 'ARS') => {
  if (val == null || isNaN(val)) return currency === 'USD' ? 'U$D 0' : '$0';
  const symbol = currency === 'USD' ? 'U$D ' : '$';
  const abs = Math.abs(val);
  const sign = val < 0 ? '-' : '';
  if (abs >= 1_000_000_000) return `${sign}${symbol}${(abs / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `${sign}${symbol}${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}${symbol}${(abs / 1_000).toFixed(1)}K`;
  return `${sign}${symbol}${abs.toFixed(0)}`;
};

const monthLabel = (key) => {
  const parts = key.split('-');
  const names = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  return `${names[parseInt(parts[1]) - 1]} ${parts[0].slice(2)}`;
};

// Build month key list ending at (year, month) going back N months (inclusive)
const monthKeysEnding = (endYear, endMonth, count) => {
  const out = [];
  let y = endYear, m = endMonth;
  for (let i = 0; i < count; i++) {
    out.unshift(`${y}-${String(m).padStart(2, '0')}`);
    m -= 1;
    if (m < 1) { m = 12; y -= 1; }
  }
  return out;
};

// Flatten ALL campaigns from all years for a given unit
const flattenCampaigns = (yearsMap, unit) => {
  const all = [];
  Object.values(yearsMap || {}).forEach((y) => {
    const src = y?.[unit];
    if (src?.campaigns) all.push(...src.campaigns);
  });
  return all;
};

// Selection helpers — "selection" is either YYYY-MM or YYYY (year-total mode)
const isYearSelection = (sel) => typeof sel === 'string' && /^\d{4}$/.test(sel);
const monthsForSelection = (sel, availableMonths) => {
  if (!sel) return [];
  if (isYearSelection(sel)) {
    return (availableMonths || []).filter((m) => m.startsWith(sel + '-'));
  }
  return [sel];
};

// Compute per-month metrics from a flat campaign array. unit decides cost currency handling.
const buildMonthlyMetrics = (campaigns, unit, rateFor) => {
  const map = {};
  for (const c of campaigns) {
    if (!c.month) continue;
    if (!map[c.month]) map[c.month] = { facturacion: 0, costo: 0, campanas: 0, ticketSum: 0, ticketCount: 0 };
    const valor = c.valor || 0;
    map[c.month].facturacion += valor;
    map[c.month].campanas += 1;
    if (!c.excluir_ticket) {
      map[c.month].ticketSum += valor;
      map[c.month].ticketCount += 1;
    }
    if (unit === 'ar') {
      map[c.month].costo += c.costo_ars || 0;
    } else {
      const usd = c.costo_usd || 0;
      const ars = c.costo_ars || 0;
      const arsToUsd = ars > 0 ? ars / rateFor(c.month) : 0;
      map[c.month].costo += usd + arsToUsd;
    }
  }
  Object.values(map).forEach((d) => {
    d.beneficio = d.facturacion - d.costo;
    d.margen = d.facturacion > 0 ? (d.beneficio / d.facturacion) * 100 : 0;
    d.ticket = d.ticketCount > 0 ? d.ticketSum / d.ticketCount : 0;
  });
  return map;
};

// ===== Trend arrow =====
const TrendArrow = ({ delta, inverse = false }) => {
  // inverse: cost-like metrics where lower is better
  if (delta == null || !isFinite(delta) || Math.abs(delta) < 0.5) {
    return (
      <span className="inline-flex items-center text-gray-500 text-[13px] font-semibold gap-0.5">
        <Minus size={14} />0%
      </span>
    );
  }
  const positive = delta > 0;
  const good = inverse ? !positive : positive;
  const color = good ? '#1B6B3A' : '#E63946';
  const Icon = positive ? ArrowUpRight : ArrowDownRight;
  return (
    <span className="inline-flex items-center text-[13px] font-bold gap-0.5" style={{ color }}>
      <Icon size={14} />{Math.abs(delta).toFixed(1)}%
    </span>
  );
};

// ===== Metric Card =====
const MetricCard = ({
  title, icon: Icon, accent, currency, format,
  values, // { avg6, prev, current, sparkData: [{label, v}] }
  inverse = false, // for "costo" — increases are bad
  highlight = false, // beneficio card
  isCount = false,
}) => {
  const formatVal = format || ((v) => fmt(v, currency));
  const renderVal = (v) => isCount ? Math.round(v || 0).toLocaleString('es-AR') : formatVal(v);

  const dVsPrev = values.prev > 0 ? ((values.current - values.prev) / values.prev) * 100 : null;
  const dVsAvg = values.avg6 > 0 ? ((values.current - values.avg6) / values.avg6) * 100 : null;

  // Sparkline color
  const sparkColor = highlight ? '#1B6B3A' : accent;

  return (
    <Card className="border-gray-800 hover:border-gray-700 transition-colors" style={{ backgroundColor: highlight ? '#0c1410' : '#111' }}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${accent}22`, border: `1px solid ${accent}55` }}
            >
              <Icon size={16} style={{ color: accent }} />
            </div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-300">
              {title}
            </h3>
          </div>
        </div>

        {/* Hero number: current month */}
        <div className="mb-2">
          <p
            className="text-4xl font-bold leading-none mb-1"
            style={{ color: highlight ? '#1B6B3A' : '#fff', fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.01em' }}
          >
            {renderVal(values.current)}
          </p>
          <p className="text-[10px] uppercase tracking-wider text-gray-500">{monthLabel(values.currentKey)}</p>
        </div>

        {/* Sparkline */}
        <div className="h-12 -mx-2 my-2">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={values.sparkData}>
              <defs>
                <linearGradient id={`spark-${title}-${accent}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={sparkColor} stopOpacity={0.55} />
                  <stop offset="100%" stopColor={sparkColor} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="v"
                stroke={sparkColor}
                strokeWidth={2}
                fill={`url(#spark-${title}-${accent})`}
                isAnimationActive={false}
                dot={false}
              />
              <Tooltip
                contentStyle={{ backgroundColor: '#000', border: '1px solid #333', fontSize: '11px', padding: '4px 8px' }}
                labelStyle={{ color: '#aaa' }}
                itemStyle={{ color: sparkColor }}
                formatter={(v) => [renderVal(v), title]}
                labelFormatter={(l) => l}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Comparison rows */}
        <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-800">
          <div>
            <p className="text-[15px] uppercase tracking-wider text-gray-400 font-semibold mb-1">Mes anterior</p>
            <p className="text-[22px] font-semibold text-gray-100 leading-tight">{renderVal(values.prev)}</p>
            <p className="text-[14px] text-gray-500">{monthLabel(values.prevKey)}</p>
            <div className="mt-1.5 flex items-center gap-1.5">
              <TrendArrow delta={dVsPrev} inverse={inverse} />
              <span className="text-[13px] text-gray-500">vs ant.</span>
            </div>
          </div>
          <div>
            <p className="text-[15px] uppercase tracking-wider text-gray-400 font-semibold mb-1">Prom. 6m</p>
            <p className="text-[22px] font-semibold text-gray-100 leading-tight">{renderVal(values.avg6)}</p>
            <p className="text-[14px] text-gray-500">{values.avg6Range}</p>
            <div className="mt-1.5 flex items-center gap-1.5">
              <TrendArrow delta={dVsAvg} inverse={inverse} />
              <span className="text-[13px] text-gray-500">vs prom.</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ===== Margin Donut =====
const MarginDonut = ({ accent, currency, current, prev, avg6, currentKey, prevKey, avg6Range }) => {
  const data = [{ name: 'Margen', value: Math.max(0, Math.min(100, current || 0)), fill: accent }];
  const dVsPrev = prev != null && Math.abs(prev) > 0.01 ? current - prev : null;
  const dVsAvg = avg6 != null && Math.abs(avg6) > 0.01 ? current - avg6 : null;

  return (
    <Card className="border-gray-800" style={{ backgroundColor: '#111' }}>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${accent}22`, border: `1px solid ${accent}55` }}
          >
            <Target size={16} style={{ color: accent }} />
          </div>
          <h3 className="text-xs font-bold uppercase tracking-wider text-gray-300">Margen %</h3>
        </div>
        <div className="relative h-32 mb-2">
          <ResponsiveContainer width="100%" height="100%">
            <RadialBarChart innerRadius="70%" outerRadius="100%" data={data} startAngle={90} endAngle={-270}>
              <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
              <RadialBar background={{ fill: '#222' }} dataKey="value" cornerRadius={8} angleAxisId={0} />
            </RadialBarChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <p className="text-3xl font-bold" style={{ color: accent, fontFamily: "'Bebas Neue', sans-serif" }}>
              {(current || 0).toFixed(1)}%
            </p>
            <p className="text-[10px] text-gray-500 uppercase">{monthLabel(currentKey)}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-800">
          <div>
            <p className="text-[15px] uppercase tracking-wider text-gray-400 font-semibold mb-1">Mes ant.</p>
            <p className="text-[22px] font-semibold text-gray-100 leading-tight">{(prev || 0).toFixed(1)}%</p>
            <p className="text-[14px] text-gray-500">{monthLabel(prevKey)}</p>
            {dVsPrev != null && (
              <p className="text-[14px] mt-1 font-semibold" style={{ color: dVsPrev >= 0 ? '#1B6B3A' : '#E63946' }}>
                {dVsPrev >= 0 ? '+' : ''}{dVsPrev.toFixed(1)} pts
              </p>
            )}
          </div>
          <div>
            <p className="text-[15px] uppercase tracking-wider text-gray-400 font-semibold mb-1">Prom. 6m</p>
            <p className="text-[22px] font-semibold text-gray-100 leading-tight">{(avg6 || 0).toFixed(1)}%</p>
            <p className="text-[14px] text-gray-500">{avg6Range}</p>
            {dVsAvg != null && (
              <p className="text-[14px] mt-1 font-semibold" style={{ color: dVsAvg >= 0 ? '#1B6B3A' : '#E63946' }}>
                {dVsAvg >= 0 ? '+' : ''}{dVsAvg.toFixed(1)} pts
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ===== Region Block =====
const RegionBlock = ({ title, unit, accent, currency, yearsMap, rateFor, pendingItems, selectedMonth }) => {
  const block = useMemo(() => {
    const campaigns = flattenCampaigns(yearsMap, unit);
    const monthly = buildMonthlyMetrics(campaigns, unit, rateFor);

    // Resolve the "focus" month — user-picked or current system month as default
    let focusY, focusM;
    if (selectedMonth) {
      const [y, m] = selectedMonth.split('-');
      focusY = parseInt(y, 10);
      focusM = parseInt(m, 10);
    } else {
      const now = new Date();
      focusY = now.getFullYear();
      focusM = now.getMonth() + 1;
    }
    const currentKey = `${focusY}-${String(focusM).padStart(2, '0')}`;
    // Previous month relative to focus
    let prevY = focusY, prevM = focusM - 1;
    if (prevM < 1) { prevM = 12; prevY -= 1; }
    const prevKey = `${prevY}-${String(prevM).padStart(2, '0')}`;
    // 6 months ending at prev (exclude focus month)
    const sixKeys = monthKeysEnding(prevY, prevM, 6);
    // Sparkline: 8 months ending at the focus month (inclusive)
    const sparkKeys = monthKeysEnding(focusY, focusM, 8);

    const get = (key, metric) => monthly[key] ? monthly[key][metric] || 0 : 0;
    const avgOf = (keys, metric) => keys.reduce((s, k) => s + get(k, metric), 0) / keys.length;
    const avgRange = `${monthLabel(sixKeys[0])} → ${monthLabel(sixKeys[5])}`;

    const buildSpark = (metric) => sparkKeys.map((k) => ({ label: monthLabel(k), v: get(k, metric) }));

    const metrics = ['facturacion', 'costo', 'beneficio', 'campanas', 'ticket'].reduce((acc, m) => {
      acc[m] = {
        current: get(currentKey, m),
        prev: get(prevKey, m),
        avg6: avgOf(sixKeys, m),
        sparkData: buildSpark(m),
        currentKey, prevKey, avg6Range: avgRange,
      };
      return acc;
    }, {});

    // Margin per period (computed from facturacion/beneficio)
    const marginAt = (key) => {
      const f = get(key, 'facturacion');
      return f > 0 ? (get(key, 'beneficio') / f) * 100 : 0;
    };
    const margenAvg6 = (() => {
      const totalF = sixKeys.reduce((s, k) => s + get(k, 'facturacion'), 0);
      const totalB = sixKeys.reduce((s, k) => s + get(k, 'beneficio'), 0);
      return totalF > 0 ? (totalB / totalF) * 100 : 0;
    })();
    const margen = {
      current: marginAt(currentKey),
      prev: marginAt(prevKey),
      avg6: margenAvg6,
      currentKey, prevKey, avg6Range: avgRange,
    };

    // Top 3 influencers (current month, by facturación)
    const buildTop = (key) => {
      const map = {};
      for (const c of campaigns) {
        if (c.month !== key) continue;
        const name = (c.influencer || '').trim();
        if (!name) continue;
        if (!map[name]) map[name] = { influencer: name, facturacion: 0, costo: 0, campanas: 0 };
        map[name].facturacion += c.valor || 0;
        map[name].campanas += 1;
        if (unit === 'ar') {
          map[name].costo += c.costo_ars || 0;
        } else {
          const usd = c.costo_usd || 0;
          const ars = c.costo_ars || 0;
          map[name].costo += usd + (ars > 0 ? ars / rateFor(c.month) : 0);
        }
      }
      return Object.values(map)
        .map((r) => ({
          ...r,
          beneficio: r.facturacion - r.costo,
          margen: r.facturacion > 0 ? ((r.facturacion - r.costo) / r.facturacion) * 100 : 0,
        }))
        .sort((a, b) => b.facturacion - a.facturacion)
        .slice(0, 3);
    };
    const topCurrent = buildTop(currentKey);
    const topPrev = buildTop(prevKey);

    return { metrics, margen, currentKey, prevKey, topCurrent, topPrev };
  }, [yearsMap, unit, rateFor, selectedMonth]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="h-7 w-1.5 rounded-full" style={{ backgroundColor: accent }} />
        <h2 className="text-3xl font-bold text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.06em' }}>
          {title}
        </h2>
        <span className="text-xs text-gray-500 font-mono">{currency}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        <MetricCard
          title="Facturación" icon={TrendingUp} accent={accent} currency={currency}
          values={block.metrics.facturacion}
        />
        <MetricCard
          title="Costo" icon={Coins} accent="#E63946" currency={currency}
          values={block.metrics.costo} inverse
        />
        <MetricCard
          title="Beneficio" icon={Receipt} accent={accent} currency={currency}
          values={block.metrics.beneficio} highlight
        />
        <MetricCard
          title="Cantidad de Campañas" icon={Activity} accent={accent} currency={currency}
          values={block.metrics.campanas} isCount
        />
        <MetricCard
          title="Ticket Promedio" icon={TrendingUp} accent="#FFD166" currency={currency}
          values={block.metrics.ticket}
        />
        <MarginDonut accent={accent} currency={currency} {...block.margen} />
        <PendingInvoiceKpi accent={accent} currency={currency} items={pendingItems || []} unit={unit} rateFor={rateFor} />
      </div>

      {/* Top 3 Influencers — Current Month vs Previous */}
      <TopInfluencersCard
        accent={accent}
        currency={currency}
        currentKey={block.currentKey}
        prevKey={block.prevKey}
        topCurrent={block.topCurrent}
        topPrev={block.topPrev}
      />
    </div>
  );
};

// ===== Top 3 Influencers Card =====
const TopInfluencersCard = ({ accent, currency, currentKey, prevKey, topCurrent, topPrev }) => {
  const renderColumn = (title, monthKey, rows) => (
    <div>
      <div className="flex items-baseline justify-between mb-3">
        <h4 className="text-xs uppercase tracking-wider text-gray-500 font-semibold">{title}</h4>
        <span className="text-[10px] text-gray-600">{monthLabel(monthKey)}</span>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-600 py-6 text-center italic">Sin datos para {monthLabel(monthKey)}</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r, i) => {
            const maxFact = Math.max(...rows.map((x) => x.facturacion)) || 1;
            const pct = (r.facturacion / maxFact) * 100;
            const rankColors = ['#FFD166', '#C0C0C0', '#CD7F32'];
            return (
              <div key={i} className="relative overflow-hidden rounded-lg border border-gray-800 bg-gray-900/60 p-3">
                <div
                  className="absolute inset-y-0 left-0 opacity-15"
                  style={{ width: `${pct}%`, backgroundColor: accent }}
                />
                <div className="relative flex items-center gap-3">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm"
                    style={{
                      backgroundColor: `${rankColors[i]}25`,
                      color: rankColors[i],
                      border: `1.5px solid ${rankColors[i]}80`,
                      fontFamily: "'Bebas Neue', sans-serif",
                    }}
                  >
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{r.influencer}</p>
                    <p className="text-[10px] text-gray-500">
                      {r.campanas} {r.campanas === 1 ? 'campaña' : 'campañas'} · Margen {r.margen.toFixed(0)}%
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold" style={{ color: accent, fontFamily: "'Bebas Neue', sans-serif" }}>
                      {fmt(r.facturacion, currency)}
                    </p>
                    <p className="text-[10px]" style={{ color: r.beneficio >= 0 ? '#1B6B3A' : '#E63946' }}>
                      Benef. {fmt(r.beneficio, currency)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <Card className="border-gray-800 mt-3" style={{ backgroundColor: '#111' }}>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${accent}22`, border: `1px solid ${accent}55` }}
          >
            <Crown size={16} style={{ color: accent }} />
          </div>
          <div>
            <h3 className="text-base font-bold text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}>
              Top 3 Influencers por Facturación
            </h3>
            <p className="text-[11px] text-gray-500">Comparativa mes en curso vs mes anterior</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {renderColumn(monthLabel(currentKey), currentKey, topCurrent)}
          {renderColumn(monthLabel(prevKey), prevKey, topPrev)}
        </div>
      </CardContent>
    </Card>
  );
};

// ===== Activations card =====
const ActivationsCard = ({ data, selectedMonth }) => {
  if (!data || !data.months) {
    return (
      <Card className="border-gray-800" style={{ backgroundColor: '#111' }}>
        <CardContent className="p-5">
          <p className="text-xs text-gray-500">Sin datos de activaciones</p>
        </CardContent>
      </Card>
    );
  }
  const currentKey = selectedMonth || data.current_month;
  const months = data.months || {};
  const currentInfo = months[currentKey];

  // Previous month key
  const [yStr, mStr] = currentKey.split('-');
  let prevY = parseInt(yStr, 10), prevM = parseInt(mStr, 10) - 1;
  if (prevM < 1) { prevM = 12; prevY -= 1; }
  const prevKey = `${prevY}-${String(prevM).padStart(2, '0')}`;
  const prevInfo = months[prevKey];

  // Sparkline 8 months ending at the focus month (inclusive)
  const sparkKeys = [];
  let sy = parseInt(yStr, 10), sm = parseInt(mStr, 10);
  for (let i = 0; i < 8; i++) {
    sparkKeys.unshift(`${sy}-${String(sm).padStart(2, '0')}`);
    sm -= 1; if (sm < 1) { sm = 12; sy -= 1; }
  }
  const sparkData = sparkKeys.map((k) => ({ label: monthLabel(k), v: months[k]?.percent || 0 }));

  const currentPct = currentInfo?.percent || 0;
  const prevPct = prevInfo?.percent || 0;
  const dPts = prevInfo ? currentPct - prevPct : null;

  return (
    <Card className="border-gray-800" style={{ backgroundColor: '#111' }}>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: '#457B9D22', border: '1px solid #457B9D55' }}
          >
            <UserCheck size={16} style={{ color: '#457B9D' }} />
          </div>
          <h3 className="text-xs font-bold uppercase tracking-wider text-gray-300">
            % Activaciones del Roster
          </h3>
        </div>

        <p
          className="text-4xl font-bold leading-none mb-1"
          style={{ color: '#457B9D', fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.01em' }}
        >
          {currentPct.toFixed(1)}%
        </p>
        <p className="text-[11px] uppercase tracking-wider text-gray-500">
          {monthLabel(currentKey)} · {currentInfo?.active_count || 0}/{currentInfo?.roster_count || 0} talentos
        </p>

        {/* Sparkline */}
        <div className="h-12 -mx-2 my-2">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={sparkData}>
              <defs>
                <linearGradient id="spark-activations" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#457B9D" stopOpacity={0.55} />
                  <stop offset="100%" stopColor="#457B9D" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="v" stroke="#457B9D" strokeWidth={2} fill="url(#spark-activations)" isAnimationActive={false} dot={false} />
              <Tooltip
                contentStyle={{ backgroundColor: '#000', border: '1px solid #333', fontSize: '11px', padding: '4px 8px' }}
                labelStyle={{ color: '#aaa' }}
                itemStyle={{ color: '#457B9D' }}
                formatter={(v) => [`${(v || 0).toFixed(1)}%`, '% Activaciones']}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-800">
          <div>
            <p className="text-[15px] uppercase tracking-wider text-gray-400 font-semibold mb-1">Mes anterior</p>
            <p className="text-[22px] font-semibold text-gray-100 leading-tight">{prevPct.toFixed(1)}%</p>
            <p className="text-[14px] text-gray-500">{monthLabel(prevKey)} (fijo)</p>
            {dPts != null && (
              <p className="text-[14px] mt-1 font-semibold" style={{ color: dPts >= 0 ? '#1B6B3A' : '#E63946' }}>
                {dPts >= 0 ? '+' : ''}{dPts.toFixed(1)} pts
              </p>
            )}
          </div>
          <div>
            <p className="text-[15px] uppercase tracking-wider text-gray-400 font-semibold mb-1">Talentos activos</p>
            <p className="text-[22px] font-semibold text-gray-100 leading-tight">{currentInfo?.active_count || 0}</p>
            <p className="text-[14px] text-gray-500">de {currentInfo?.roster_count || 0} en roster</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ===== Year-to-date totals card =====
const YtdCard = ({ title, sublabel, value, currency, accent, onViewInvoices, invoiceCount, testId }) => (
  <Card className="border-gray-800" style={{ backgroundColor: '#111' }}>
    <CardContent className="p-5">
      <div className="flex items-center gap-2 mb-1">
        <div
          className="w-7 h-7 rounded-md flex items-center justify-center"
          style={{ backgroundColor: `${accent}22`, border: `1px solid ${accent}55` }}
        >
          <TrendingUp size={14} style={{ color: accent }} />
        </div>
        <p className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">{title}</p>
      </div>
      <p
        className="text-3xl font-bold leading-none mb-1 mt-2"
        style={{ color: accent, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.02em' }}
      >
        {fmt(value, currency)}
      </p>
      <p className="text-[12px] text-gray-500">{sublabel}</p>
      {onViewInvoices && (
        <button
          type="button"
          onClick={onViewInvoices}
          className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold hover:underline"
          style={{ color: accent }}
          data-testid={testId}
        >
          <Eye size={13} /> Ver facturas ({invoiceCount || 0})
        </button>
      )}
    </CardContent>
  </Card>
);

const buildYtd = (yearsMap, unit, year, rateFor) => {
  const campaigns = flattenCampaigns(yearsMap, unit).filter((c) => (c.month || '').startsWith(`${year}-`));
  let facturacion = 0, costo = 0;
  for (const c of campaigns) {
    facturacion += c.valor || 0;
    if (unit === 'ar') {
      costo += c.costo_ars || 0;
    } else {
      const usd = c.costo_usd || 0;
      const ars = c.costo_ars || 0;
      costo += usd + (ars > 0 ? ars / rateFor(c.month) : 0);
    }
  }
  return { facturacion, costo, beneficio: facturacion - costo };
};

// ===== Pending Invoice KPI (single card, no detail table) =====
const PendingInvoiceKpi = ({ accent, currency, items, unit, rateFor }) => {
  const total = (items || []).reduce((s, p) => s + (p.valor || 0), 0);
  const count = (items || []).length;
  const totalCost = (items || []).reduce((s, p) => {
    if (unit === 'ar') return s + (p.costo_ars || 0);
    const usd = p.costo_usd || 0;
    const ars = p.costo_ars || 0;
    return s + usd + (ars > 0 && p.campana_month ? ars / rateFor(p.campana_month) : ars / 1415);
  }, 0);
  const beneficio = total - totalCost;

  return (
    <Card className="border-gray-800" style={{ backgroundColor: '#111' }}>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: '#FFD16622', border: '1px solid #FFD16655' }}
          >
            <AlertTriangle size={16} style={{ color: '#FFD166' }} />
          </div>
          <h3 className="text-xs font-bold uppercase tracking-wider text-gray-300">
            Aprobadas Sin Facturar
          </h3>
        </div>

        <p
          className="text-4xl font-bold leading-none mb-1"
          style={{ color: '#FFD166', fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.01em' }}
        >
          {fmt(total, currency)}
        </p>
        <p className="text-[11px] uppercase tracking-wider text-gray-500">Pendiente de facturar</p>

        <div className="grid grid-cols-2 gap-3 mt-4 pt-3 border-t border-gray-800">
          <div>
            <p className="text-[15px] uppercase tracking-wider text-gray-400 font-semibold mb-1">Campañas</p>
            <p className="text-[22px] font-semibold text-gray-100 leading-tight">{count}</p>
            <p className="text-[14px] text-gray-500">aprobadas</p>
          </div>
          <div>
            <p className="text-[15px] uppercase tracking-wider text-gray-400 font-semibold mb-1">Beneficio potencial</p>
            <p className="text-[22px] font-semibold leading-tight" style={{ color: beneficio >= 0 ? '#1B6B3A' : '#E63946' }}>
              {fmt(beneficio, currency)}
            </p>
            <p className="text-[14px] text-gray-500">Costo {fmt(totalCost, currency)}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ===== P&L Module (Estado de Resultados consolidado de la agencia) =====
const PnLRow = ({ label, value, currency = 'USD', isSubtotal = false, isTotal = false, indent = 0, sublabel = null, pending = false, accent = '#FFD166' }) => {
  const cls = isTotal
    ? 'border-t-2 border-white/30 pt-3 mt-1'
    : isSubtotal
    ? 'border-t border-gray-700 pt-2 mt-1'
    : '';
  const labelCls = isTotal
    ? 'text-base font-bold uppercase tracking-wider text-white'
    : isSubtotal
    ? 'text-sm font-bold uppercase tracking-wider text-gray-200'
    : 'text-sm text-gray-300';
  const valCls = isTotal ? 'text-2xl font-bold' : isSubtotal ? 'text-lg font-bold' : 'text-base font-semibold';
  const color = pending ? '#6b7280' : value < 0 ? '#E63946' : isTotal || isSubtotal ? accent : '#e5e7eb';
  return (
    <div className={`flex items-baseline justify-between gap-4 py-2 ${cls}`} style={{ paddingLeft: `${indent * 16}px` }}>
      <div className="flex flex-col">
        <span className={labelCls} style={{ fontFamily: isTotal || isSubtotal ? "'Bebas Neue', sans-serif" : "'DM Sans', sans-serif", letterSpacing: isTotal || isSubtotal ? '0.04em' : '0' }}>
          {label}
        </span>
        {sublabel && <span className="text-[10px] text-gray-500 mt-0.5">{sublabel}</span>}
      </div>
      <span className={valCls} style={{ color, fontFamily: "'DM Sans', sans-serif" }}>
        {pending ? <span className="text-xs italic text-gray-500">pendiente</span> : fmt(value, currency)}
      </span>
    </div>
  );
};

const PnLModule = ({ yearsMap, rateFor, selectedMonth, edr, availableMonths }) => {
  const data = useMemo(() => {
    const isYear = isYearSelection(selectedMonth);
    const monthsInScope = monthsForSelection(selectedMonth, availableMonths);

    const arAll = flattenCampaigns(yearsMap, 'ar');
    const intlAll = flattenCampaigns(yearsMap, 'intl');

    // Per-month FX (prefer EDR FX, fall back to rateFor)
    const fxOf = (m) => {
      const e = edr?.fx_rates?.[m];
      return e && e > 0 ? e : rateFor(m);
    };
    // Representative FX for header display (only meaningful in single-month mode)
    const fx = isYear ? 0 : fxOf(selectedMonth);
    const edrFx = isYear ? null : edr?.fx_rates?.[selectedMonth];

    // Accumulators
    let ventaArUsd = 0, costoArUsd = 0;
    let ventaIntlUsd = 0, costoIntlUsd = 0;
    let arOpex = { sueldos: 0, comisiones: 0, alquiler: 0, gastos: 0 };
    let intlOpex = { sueldos: 0, comisiones: 0, gastos: 0 };
    let hasAnyArOpex = false, hasAnyIntlOpex = false;

    for (const m of monthsInScope) {
      const rate = fxOf(m);
      const arsToUsd = (ars) => (rate > 0 ? ars / rate : 0);

      // AR campaigns
      for (const c of arAll) {
        if (c.month !== m) continue;
        ventaArUsd += arsToUsd(c.valor || 0);
        costoArUsd += arsToUsd(c.costo_ars || 0);
      }
      // INTL campaigns
      for (const c of intlAll) {
        if (c.month !== m) continue;
        ventaIntlUsd += (c.valor || 0) + arsToUsd(c.valor_ars_extra || 0);
        costoIntlUsd += (c.costo_usd || 0) + arsToUsd(c.costo_ars || 0);
      }

      // AR OPEX (raw in ARS)
      const ar = edr?.ar_opex?.[m];
      if (ar) {
        hasAnyArOpex = true;
        arOpex.sueldos += arsToUsd(ar.sueldos_cargas || 0);
        arOpex.comisiones += arsToUsd(ar.comisiones || 0);
        arOpex.alquiler += arsToUsd(ar.alquiler || 0);
        arOpex.gastos += arsToUsd(ar.gastos_corrientes || 0);
      }
      // INTL OPEX (2026-08-13: sueldos+cargas en ARS, bonos en USD)
      const intl = edr?.intl_opex?.[m];
      if (intl) {
        hasAnyIntlOpex = true;
        intlOpex.sueldos += arsToUsd(intl.sueldos_cargas || 0);
        // Bonos Inter (fila 6 Gastos) YA en USD → no convertir.
        intlOpex.comisiones += (intl.comisiones || 0);
        intlOpex.gastos += arsToUsd(intl.gastos_corrientes || 0);
      }
    }

    const ventaTotal = ventaArUsd + ventaIntlUsd;
    const costoTotal = costoArUsd + costoIntlUsd;
    const beneficioBruto = ventaTotal - costoTotal;
    const margenBrutoPct = ventaTotal > 0 ? (beneficioBruto / ventaTotal) * 100 : 0;

    const arOpexTotal = hasAnyArOpex ? arOpex.sueldos + arOpex.comisiones + arOpex.alquiler + arOpex.gastos : 0;
    const utilidadBrutaAr = ventaArUsd - costoArUsd;
    const utilidadAr = utilidadBrutaAr - arOpexTotal;

    const intlOpexTotal = hasAnyIntlOpex ? intlOpex.sueldos + intlOpex.comisiones + intlOpex.gastos : 0;
    const utilidadBrutaIntl = ventaIntlUsd - costoIntlUsd;
    const utilidadIntl = utilidadBrutaIntl - intlOpexTotal;

    const utilidadTotal = utilidadAr + utilidadIntl;
    const margenOpTotal = ventaTotal > 0 ? (utilidadTotal / ventaTotal) * 100 : 0;
    const utilidadBrutaArPct = ventaArUsd > 0 ? (utilidadBrutaAr / ventaArUsd) * 100 : 0;
    const utilidadArPct = ventaArUsd > 0 ? (utilidadAr / ventaArUsd) * 100 : 0;
    const utilidadBrutaIntlPct = ventaIntlUsd > 0 ? (utilidadBrutaIntl / ventaIntlUsd) * 100 : 0;
    const utilidadIntlPct = ventaIntlUsd > 0 ? (utilidadIntl / ventaIntlUsd) * 100 : 0;

    return {
      isYear, monthsInScope, fx, edrFx,
      ventaArUsd, ventaIntlUsd, ventaTotal,
      costoArUsd, costoIntlUsd, costoTotal,
      beneficioBruto, margenBrutoPct,
      arOpex: hasAnyArOpex ? arOpex : null, arOpexTotal, utilidadBrutaAr, utilidadBrutaArPct, utilidadAr, utilidadArPct,
      intlOpex: hasAnyIntlOpex ? intlOpex : null, intlOpexTotal, utilidadBrutaIntl, utilidadBrutaIntlPct, utilidadIntl, utilidadIntlPct,
      utilidadTotal, margenOpTotal,
    };
  }, [yearsMap, rateFor, selectedMonth, edr, availableMonths]);

  const Pct = ({ value, accent = '#FFD166' }) => (
    <span className="text-xs font-mono ml-2" style={{ color: accent }}>
      ({(value || 0).toFixed(1)}%)
    </span>
  );

  return (
    <div>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="h-7 w-1.5 rounded-full" style={{ backgroundColor: '#FFD166' }} />
        <h2 className="text-3xl font-bold text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.06em' }}>
          Estado de Resultados — Agencia
        </h2>
        <span className="text-xs text-gray-500 font-mono">
          USD · {data.isYear
            ? `Total ${selectedMonth} (${data.monthsInScope.length} ${data.monthsInScope.length === 1 ? 'mes' : 'meses'})`
            : `${monthLabel(selectedMonth)} · TC ${data.fx.toLocaleString('es-AR', { maximumFractionDigits: 0 })}${data.edrFx ? ' (EDR)' : ' (FX panel)'}`}
        </span>
      </div>

      <Card className="border-yellow-500/20" style={{ backgroundColor: '#111' }} data-testid="pnl-module">
        <CardContent className="p-6 space-y-8">

          {/* --- TOP: CONSOLIDADO --- */}
          <div>
            <h3 className="text-xs uppercase tracking-wider text-yellow-300 font-bold mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.08em' }}>
              Consolidado (Agencia)
            </h3>
            <PnLRow label="Venta · Argentina" value={data.ventaArUsd} />
            <PnLRow label="Venta · Internacional" value={data.ventaIntlUsd} />
            <PnLRow label="Venta Total" value={data.ventaTotal} isSubtotal accent="#FFD166" />
            <PnLRow label="(−) Costo Talentos · Argentina" value={-data.costoArUsd} />
            <PnLRow label="(−) Costo Talentos · Internacional" value={-data.costoIntlUsd} />
            <PnLRow label="Total Costos Directos" value={-data.costoTotal} isSubtotal accent="#E63946" />
            <div className="border-t-2 border-yellow-500/40 pt-3 mt-3">
              <div className="flex items-baseline justify-between">
                <span className="text-base font-bold uppercase tracking-wider text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.04em' }}>
                  BENEFICIO BRUTO
                  <Pct value={data.margenBrutoPct} />
                </span>
                <span className="text-2xl font-bold" style={{ color: data.beneficioBruto >= 0 ? '#FFD166' : '#E63946', fontFamily: "'DM Sans', sans-serif" }}>
                  {fmt(data.beneficioBruto, 'USD')}
                </span>
              </div>
            </div>
          </div>

          {/* --- ARGENTINA --- */}
          <div className="border-t border-gray-800 pt-6">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-5 w-1 rounded-full" style={{ backgroundColor: '#F4A261' }} />
              <h3 className="text-base uppercase font-bold text-orange-300" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.08em' }}>
                Argentina
              </h3>
            </div>
            <PnLRow label="Venta AR" value={data.ventaArUsd} />
            <PnLRow label="(−) Costo Talentos AR" value={-data.costoArUsd} />
            <div className="flex items-baseline justify-between border-t border-gray-700 pt-2 mt-1">
              <span className="text-sm font-bold uppercase tracking-wider text-gray-200" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.04em' }}>
                Utilidad Bruta AR
                <Pct value={data.utilidadBrutaArPct} accent="#F4A261" />
              </span>
              <span className="text-lg font-bold" style={{ color: '#F4A261' }}>{fmt(data.utilidadBrutaAr, 'USD')}</span>
            </div>
            {data.arOpex ? (
              <>
                <PnLRow label="(−) Sueldos y Cargas" value={-data.arOpex.sueldos} sublabel="EDR fila 58-62 (sueldos + cargas + monotributo + autónomos)" />
                <PnLRow label="(−) Comisiones de Talentos" value={-data.arOpex.comisiones} sublabel="Bonos Comerciales mensuales" />
                <PnLRow label="(−) Alquiler" value={-data.arOpex.alquiler} sublabel="enero replicado de febrero" />
                <PnLRow label="(−) Gastos Corrientes" value={-data.arOpex.gastos} sublabel="cadetería, contador, g-suites, viáticos, comidas, big box, banco, telefonía, extraordinarios" />
              </>
            ) : (
              <PnLRow label="OPEX · Argentina" value={0} pending sublabel="cargar pestaña EDR" />
            )}
            <div className="border-t-2 border-orange-500/40 pt-3 mt-3">
              <div className="flex items-baseline justify-between">
                <span className="text-base font-bold uppercase tracking-wider text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.04em' }}>
                  Utilidad Argentina
                  <Pct value={data.utilidadArPct} accent="#F4A261" />
                </span>
                <span className="text-2xl font-bold" style={{ color: data.utilidadAr >= 0 ? '#F4A261' : '#E63946' }}>
                  {fmt(data.utilidadAr, 'USD')}
                </span>
              </div>
            </div>
          </div>

          {/* --- INTERNACIONAL --- */}
          <div className="border-t border-gray-800 pt-6">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-5 w-1 rounded-full" style={{ backgroundColor: '#FF6EC7' }} />
              <h3 className="text-base uppercase font-bold text-pink-300" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.08em' }}>
                Internacional
              </h3>
            </div>
            <PnLRow label="Venta Internacional" value={data.ventaIntlUsd} />
            <PnLRow label="(−) Costo Talentos Internacional" value={-data.costoIntlUsd} />
            <div className="flex items-baseline justify-between border-t border-gray-700 pt-2 mt-1">
              <span className="text-sm font-bold uppercase tracking-wider text-gray-200" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.04em' }}>
                Utilidad Bruta INT
                <Pct value={data.utilidadBrutaIntlPct} accent="#FF6EC7" />
              </span>
              <span className="text-lg font-bold" style={{ color: '#FF6EC7' }}>{fmt(data.utilidadBrutaIntl, 'USD')}</span>
            </div>
            {data.intlOpex ? (
              <>
                <PnLRow label="(−) Sueldos y Cargas" value={-data.intlOpex.sueldos} sublabel="EDR fila 22-24 (sueldos + Sueldos U$ + monotributo)" />
                <PnLRow label="(−) Comisiones de Talentos" value={-data.intlOpex.comisiones} sublabel="Bonos Comerciales — pagados cada 3 meses" />
                <PnLRow label="(−) Gastos Corrientes" value={-data.intlOpex.gastos} sublabel="g-suites + extraordinarios" />
              </>
            ) : (
              <PnLRow label="OPEX · Internacional" value={0} pending sublabel="cargar pestaña EDR" />
            )}
            <div className="border-t-2 border-pink-500/40 pt-3 mt-3">
              <div className="flex items-baseline justify-between">
                <span className="text-base font-bold uppercase tracking-wider text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.04em' }}>
                  Utilidad Internacional
                  <Pct value={data.utilidadIntlPct} accent="#FF6EC7" />
                </span>
                <span className="text-2xl font-bold" style={{ color: data.utilidadIntl >= 0 ? '#FF6EC7' : '#E63946' }}>
                  {fmt(data.utilidadIntl, 'USD')}
                </span>
              </div>
            </div>
          </div>

          {/* --- RESULTADO UNIFICADO --- */}
          <div className="border-t-2 border-white/20 pt-6 mt-6">
            <div className="p-5 rounded-lg" style={{ backgroundColor: data.utilidadTotal >= 0 ? '#1B6B3A22' : '#E6394622', border: `2px solid ${data.utilidadTotal >= 0 ? '#1B6B3A' : '#E63946'}` }}>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <p className="text-xs uppercase tracking-widest text-gray-300 font-bold" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.12em' }}>
                    Resultado Unificado — Talengers
                  </p>
                  <p className="text-[11px] text-gray-500 mt-1">Utilidad AR + Utilidad Internacional</p>
                </div>
                <div className="text-right">
                  <p className="text-4xl font-bold" style={{ color: data.utilidadTotal >= 0 ? '#1B6B3A' : '#E63946', fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.02em' }}>
                    {fmt(data.utilidadTotal, 'USD')}
                  </p>
                  <p className="text-sm font-semibold mt-1" style={{ color: data.margenOpTotal >= 0 ? '#1B6B3A' : '#E63946' }}>
                    Margen Operativo Consolidado: {data.margenOpTotal.toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>
          </div>

        </CardContent>
      </Card>
    </div>
  );
};

// ===== Main =====
const ResumenView = ({ yearsMap, rateFor, onGoDetail, lastUpdated, isAr, isIntl, pendingInvoice, activations, edr }) => {
  const currentYear = new Date().getFullYear().toString();
  const ytdAr = useMemo(() => buildYtd(yearsMap, 'ar', currentYear, rateFor), [yearsMap, currentYear, rateFor]);
  const ytdIntl = useMemo(() => buildYtd(yearsMap, 'intl', currentYear, rateFor), [yearsMap, currentYear, rateFor]);

  // Facturas (campañas) que componen el acumulado del año en curso, por unidad.
  // Son exactamente las filas que se suman para armar las tarjetas "Facturación AÑO".
  const ytdArInvoices = useMemo(
    () => flattenCampaigns(yearsMap, 'ar')
      .filter((c) => (c.month || '').startsWith(`${currentYear}-`))
      .sort((a, b) => (a.fecha || '').localeCompare(b.fecha || '')),
    [yearsMap, currentYear]
  );
  const ytdIntlInvoices = useMemo(
    () => flattenCampaigns(yearsMap, 'intl')
      .filter((c) => (c.month || '').startsWith(`${currentYear}-`))
      .sort((a, b) => (a.fecha || '').localeCompare(b.fecha || '')),
    [yearsMap, currentYear]
  );

  // Modal "Ver facturas" — lista completa + export Excel/PDF
  const [invoiceModal, setInvoiceModal] = useState(null); // { unit, title, currency, accent, invoices }
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoiceMonthFilter, setInvoiceMonthFilter] = useState('all');
  const [invoiceNcOnly, setInvoiceNcOnly] = useState(false);

  // Meses disponibles dentro del set de facturas del modal abierto
  const invoiceMonthOptions = useMemo(() => {
    if (!invoiceModal) return [];
    const set = new Set((invoiceModal.invoices || []).map((inv) => inv.month).filter(Boolean));
    return Array.from(set).sort();
  }, [invoiceModal]);

  // Facturas del modal ya filtradas por mes + "Solo NC" + búsqueda de texto
  const filteredInvoices = useMemo(() => {
    if (!invoiceModal) return [];
    const q = invoiceSearch.trim().toLowerCase();
    return invoiceModal.invoices.filter((inv) => {
      if (invoiceMonthFilter !== 'all' && inv.month !== invoiceMonthFilter) return false;
      if (invoiceNcOnly && !((inv.valor || 0) < 0)) return false;
      if (q) {
        const hit = String(inv.nro_factura || '').toLowerCase().includes(q) ||
          String(inv.marca || '').toLowerCase().includes(q) ||
          String(inv.agencia || '').toLowerCase().includes(q) ||
          String(inv.influencer || '').toLowerCase().includes(q) ||
          String(inv.kam || '').toLowerCase().includes(q);
        if (!hit) return false;
      }
      return true;
    });
  }, [invoiceModal, invoiceSearch, invoiceMonthFilter, invoiceNcOnly]);

  const buildInvoiceRows = useCallback((invoices) => invoices.map((inv) => ({
    Mes: monthLabel(inv.month),
    Fecha: (inv.fecha || '').slice(0, 10),
    'Nº Factura': inv.nro_factura || '',
    Marca: inv.marca || '',
    Agencia: inv.agencia || '',
    Influencer: inv.influencer || '',
    Comercial: inv.kam || '',
    Valor: Math.round(inv.valor || 0),
  })), []);

  const exportInvoicesToExcel = useCallback((modal, invoices, suffix = '') => {
    const rows = buildInvoiceRows(invoices);
    const total = rows.reduce((s, r) => s + (r.Valor || 0), 0);
    const withTotal = [...rows, {}, { Mes: 'TOTAL', Valor: total }];
    const ws = XLSX.utils.json_to_sheet(withTotal);
    const headers = Object.keys(rows[0] || { Mes: '', Fecha: '', 'Nº Factura': '', Marca: '', Agencia: '', Influencer: '', Comercial: '', Valor: '' });
    ws['!cols'] = headers.map((h) => ({ wch: Math.max(h.length, ...withTotal.map((r) => String(r[h] ?? '').length)) + 2 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `${modal.unit.toUpperCase()}-${currentYear}`);
    XLSX.writeFile(wb, `facturas-${modal.unit}-${currentYear}${suffix}.xlsx`);
  }, [buildInvoiceRows, currentYear]);

  const exportInvoicesToPDF = useCallback((modal, invoices, suffix = '') => {
    const rows = buildInvoiceRows(invoices);
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const fmtN = (n) => new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(n);
    const pageW = doc.internal.pageSize.getWidth();
    const BRAND = [27, 107, 58];
    const total = rows.reduce((s, r) => s + (r.Valor || 0), 0);

    doc.setFillColor(...BRAND);
    doc.rect(0, 0, pageW, 56, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(255, 255, 255);
    doc.text(modal.title, 40, 28);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`${rows.length} facturas · Total ${modal.currency === 'USD' ? 'U$D' : '$'} ${fmtN(total)}`, 40, 44);

    autoTable(doc, {
      startY: 72,
      head: [['Mes', 'Fecha', 'Nº Fact', 'Marca', 'Agencia', 'Influencer', 'Comercial', 'Valor']],
      body: rows.map((r) => [r.Mes, r.Fecha, r['Nº Factura'] || '—', r.Marca, r.Agencia, r.Influencer, r.Comercial, `${modal.currency === 'USD' ? 'U$D' : '$'} ${fmtN(r.Valor)}`]),
      foot: [['', '', '', '', '', '', 'TOTAL', `${modal.currency === 'USD' ? 'U$D' : '$'} ${fmtN(total)}`]],
      styles: { fontSize: 8, cellPadding: 3, overflow: 'linebreak' },
      headStyles: { fillColor: BRAND, textColor: 255, fontStyle: 'bold' },
      footStyles: { fillColor: [30, 30, 30], textColor: 255, fontStyle: 'bold' },
      columnStyles: { 7: { halign: 'right' } },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { left: 40, right: 40, bottom: 40 },
    });
    doc.save(`facturas-${modal.unit}-${currentYear}${suffix}.pdf`);
  }, [buildInvoiceRows, currentYear]);

  // Available months across all years/units — filtered to 2025+ only
  // (no confiable data for 2024 / 2023, so we hide them from the dropdown).
  const MIN_YEAR = 2025;
  const availableMonths = useMemo(() => {
    const set = new Set();
    Object.values(yearsMap || {}).forEach((y) => {
      ['ar', 'intl'].forEach((u) => {
        (y?.[u]?.campaigns || []).forEach((c) => {
          if (!c.month) return;
          const yr = parseInt(c.month.slice(0, 4), 10);
          if (yr >= MIN_YEAR) set.add(c.month);
        });
      });
    });
    return Array.from(set).sort().reverse();
  }, [yearsMap]);

  // Available years derived from months (for year-total selections)
  const availableYears = useMemo(() => {
    const yset = new Set(availableMonths.map((m) => m.slice(0, 4)));
    return Array.from(yset).sort().reverse();
  }, [availableMonths]);

  // Default: current system month (or newest available if no current month exists)
  const now = new Date();
  const systemMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const defaultMonth = availableMonths.includes(systemMonth)
    ? systemMonth
    : (availableMonths[0] || systemMonth);
  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);

  // For RegionBlock / ActivationsCard which expect YYYY-MM:
  // if user picks a year, fall back to the latest month with data in that year.
  const effectiveMonth = useMemo(() => {
    if (!isYearSelection(selectedMonth)) return selectedMonth;
    const inYear = (availableMonths || []).filter((m) => m.startsWith(selectedMonth + '-'));
    return inYear[0] || selectedMonth; // availableMonths is sorted desc → [0] is latest
  }, [selectedMonth, availableMonths]);

  // Keep default in sync if data changes after first render
  useEffect(() => {
    const isValid = availableMonths.includes(selectedMonth) || availableYears.includes(selectedMonth);
    if (availableMonths.length && !isValid) {
      setSelectedMonth(defaultMonth);
    }
  }, [availableMonths, availableYears, defaultMonth, selectedMonth]);

  return (
    <div className="px-6 py-6 space-y-10">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl font-bold text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.06em' }}>
            Resumen Ejecutivo
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Comparación contra el mes anterior y el promedio de los últimos 6 meses cerrados.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-xs uppercase tracking-wider text-gray-400 font-semibold" htmlFor="resumen-month-select">
              Mes
            </label>
            <select
              id="resumen-month-select"
              data-testid="resumen-month-select"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="bg-gray-900 border border-gray-700 text-white text-lg font-semibold px-4 py-3 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400 min-w-[220px]"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              <optgroup label="Total anual">
                {availableYears.map((y) => (
                  <option key={`year-${y}`} value={y}>Total {y}</option>
                ))}
              </optgroup>
              <optgroup label="Por mes">
                {availableMonths.map((m) => (
                  <option key={m} value={m}>{monthLabel(m)}</option>
                ))}
              </optgroup>
            </select>
          </div>
          <Button
            onClick={onGoDetail}
            className="text-white font-semibold gap-2"
            style={{ backgroundColor: '#457B9D' }}
            data-testid="resumen-go-detail-btn"
          >
            Ver dashboard detallado <ArrowRight size={16} />
          </Button>
        </div>
      </div>

      {/* YTD Totals */}
      <div>
        <h2 className="text-xl font-bold text-white mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}>
          Totales {currentYear} <span className="text-xs text-gray-500 font-mono ml-2">acumulado año en curso</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          <YtdCard
            title={`Facturación ${currentYear} · AR`} sublabel="Acumulado nacional" value={ytdAr.facturacion} currency="ARS" accent="#F4A261"
            invoiceCount={ytdArInvoices.length}
            testId="resumen-ytd-view-invoices-ar"
            onViewInvoices={() => setInvoiceModal({ unit: 'ar', title: `Facturación ${currentYear} · Argentina`, currency: 'ARS', accent: '#F4A261', invoices: ytdArInvoices })}
          />
          <YtdCard
            title={`Facturación ${currentYear} · INTL`} sublabel="Acumulado internacional" value={ytdIntl.facturacion} currency="USD" accent="#FF6EC7"
            invoiceCount={ytdIntlInvoices.length}
            testId="resumen-ytd-view-invoices-intl"
            onViewInvoices={() => setInvoiceModal({ unit: 'intl', title: `Facturación ${currentYear} · Internacional`, currency: 'USD', accent: '#FF6EC7', invoices: ytdIntlInvoices })}
          />
          <YtdCard title={`Beneficio ${currentYear} · AR`} sublabel="Facturación − Costo" value={ytdAr.beneficio} currency="ARS" accent="#1B6B3A" />
          <YtdCard title={`Beneficio ${currentYear} · INTL`} sublabel="Facturación − Costo (USD)" value={ytdIntl.beneficio} currency="USD" accent="#1B6B3A" />
          <ActivationsCard data={activations} selectedMonth={effectiveMonth} />
        </div>
      </div>

      {isAr && (
        <RegionBlock title="Argentina" unit="ar" accent="#F4A261" currency="ARS" yearsMap={yearsMap} rateFor={rateFor} pendingItems={pendingInvoice?.ar || []} selectedMonth={effectiveMonth} />
      )}
      {isIntl && (
        <RegionBlock title="Internacional" unit="intl" accent="#FF6EC7" currency="USD" yearsMap={yearsMap} rateFor={rateFor} pendingItems={pendingInvoice?.intl || []} selectedMonth={effectiveMonth} />
      )}

      {/* Modal: todas las facturas que componen el acumulado del año (AR o INTL) */}
      <Dialog open={!!invoiceModal} onOpenChange={(o) => { if (!o) { setInvoiceModal(null); setInvoiceSearch(''); setInvoiceMonthFilter('all'); setInvoiceNcOnly(false); } }}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="resumen-invoices-modal">
          <DialogHeader>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <DialogTitle className="text-2xl" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.04em' }}>
                {invoiceModal?.title}
              </DialogTitle>
              {invoiceModal && invoiceModal.invoices.length > 0 && (
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    onClick={() => exportInvoicesToExcel(
                      invoiceModal, filteredInvoices,
                      `${invoiceMonthFilter !== 'all' ? `-${invoiceMonthFilter}` : ''}${invoiceNcOnly ? '-NC' : ''}`
                    )}
                    size="sm"
                    variant="outline"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-700 h-8"
                    data-testid="resumen-invoices-export-excel"
                  >
                    <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />
                    Excel
                  </Button>
                  <Button
                    onClick={() => exportInvoicesToPDF(
                      invoiceModal, filteredInvoices,
                      `${invoiceMonthFilter !== 'all' ? `-${invoiceMonthFilter}` : ''}${invoiceNcOnly ? '-NC' : ''}`
                    )}
                    size="sm"
                    variant="outline"
                    className="bg-red-600 hover:bg-red-700 text-white border-red-700 h-8"
                    data-testid="resumen-invoices-export-pdf"
                  >
                    <FileText className="w-3.5 h-3.5 mr-1.5" />
                    PDF
                  </Button>
                </div>
              )}
            </div>
            {invoiceModal && (
              <div className="text-xs text-gray-400 flex flex-wrap gap-3 mt-3 items-center">
                <span>Cantidad total: <strong className="text-white">{invoiceModal.invoices.length}</strong></span>
                <select
                  value={invoiceMonthFilter}
                  onChange={(e) => setInvoiceMonthFilter(e.target.value)}
                  className="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-xs text-white focus:outline-none focus:border-emerald-500"
                  data-testid="resumen-invoices-month-filter"
                >
                  <option value="all">Todos los meses</option>
                  {invoiceMonthOptions.map((m) => (
                    <option key={m} value={m}>{monthLabel(m)}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setInvoiceNcOnly((v) => !v)}
                  className="px-2.5 py-1.5 rounded text-[11px] font-bold uppercase tracking-wider border transition-colors"
                  style={invoiceNcOnly
                    ? { backgroundColor: '#E6394622', color: '#E63946', borderColor: '#E63946' }
                    : { backgroundColor: 'transparent', color: '#9ca3af', borderColor: '#374151' }}
                  data-testid="resumen-invoices-nc-only-toggle"
                >
                  Solo NC
                </button>
                <div className="ml-auto relative">
                  <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
                  <input
                    type="text"
                    value={invoiceSearch}
                    onChange={(e) => setInvoiceSearch(e.target.value)}
                    placeholder="Buscar Nº factura, marca, agencia, influencer..."
                    className="pl-7 pr-3 py-1.5 text-xs bg-gray-800 border border-gray-700 rounded text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500 w-72"
                    data-testid="resumen-invoices-search"
                  />
                </div>
              </div>
            )}
          </DialogHeader>
          {invoiceModal && (() => {
            const hasFilter = !!invoiceSearch.trim() || invoiceMonthFilter !== 'all' || invoiceNcOnly;
            const totalFiltered = filteredInvoices.reduce((s, i) => s + (i.valor || 0), 0);
            return (
              <div className="overflow-auto flex-1">
                {hasFilter && (
                  <div className="px-3 py-1.5 text-[11px] text-gray-400 bg-gray-800/50 border-b border-gray-700" data-testid="resumen-invoices-filter-summary">
                    Mostrando <strong className="text-white">{filteredInvoices.length}</strong> de <strong className="text-white">{invoiceModal.invoices.length}</strong> facturas
                    {invoiceMonthFilter !== 'all' && <> · Mes: {monthLabel(invoiceMonthFilter)}</>}
                    {invoiceNcOnly && <> · Solo NC</>}
                    {invoiceSearch.trim() && <> · Filtro: "{invoiceSearch}"</>}
                  </div>
                )}
                <table className="w-full text-xs">
                  <thead className="sticky top-0" style={{ backgroundColor: '#111' }}>
                    <tr className="border-b border-neutral-800 text-[10px] uppercase tracking-wider text-gray-400">
                      <th className="px-2 py-2 text-left">Mes</th>
                      <th className="px-2 py-2 text-left">Fecha</th>
                      <th className="px-2 py-2 text-left">Nº Fact</th>
                      <th className="px-2 py-2 text-left">Marca</th>
                      <th className="px-2 py-2 text-left">Agencia</th>
                      <th className="px-2 py-2 text-left">Influencer</th>
                      <th className="px-2 py-2 text-left">Comercial</th>
                      <th className="px-2 py-2 text-right">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoices.length === 0 && (
                      <tr>
                        <td colSpan={8} className="px-2 py-6 text-center text-gray-500 text-xs">
                          Sin facturas que coincidan con los filtros aplicados.
                        </td>
                      </tr>
                    )}
                    {filteredInvoices.map((inv, idx) => (
                      <tr key={idx} className="border-b border-neutral-900 hover:bg-neutral-950/50" data-testid={`resumen-invoice-row-${idx}`}>
                        <td className="px-2 py-2 font-mono text-gray-400">{monthLabel(inv.month)}</td>
                        <td className="px-2 py-2 font-mono text-gray-300">{(inv.fecha || '').slice(0, 10)}</td>
                        <td className="px-2 py-2 font-mono text-cyan-300">{inv.nro_factura || '—'}</td>
                        <td className="px-2 py-2 text-white">{inv.marca || '—'}</td>
                        <td className="px-2 py-2 text-gray-400">{inv.agencia || '—'}</td>
                        <td className="px-2 py-2 text-gray-300">{inv.influencer || '—'}</td>
                        <td className="px-2 py-2 text-gray-400">{inv.kam || '—'}</td>
                        <td className="px-2 py-2 text-right font-mono font-bold" style={{ color: (inv.valor || 0) < 0 ? '#E63946' : '#e5e7eb' }}>
                          {fmt(inv.valor, invoiceModal.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-neutral-700 font-bold" style={{ backgroundColor: '#0f0f0f' }}>
                      <td colSpan={7} className="px-2 py-3 text-right text-gray-300 uppercase tracking-wider text-[10px]">
                        Totales{hasFilter ? ' (filtrados)' : ''}
                      </td>
                      <td className="px-2 py-3 text-right font-mono" style={{ color: invoiceModal.accent }}>
                        {fmt(totalFiltered, invoiceModal.currency)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResumenView;
